import React, { useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useUserPermissions } from 'c-auth-module/Hooks';
import {
    AutoGrid,
    Button,
    Checkbox,
    Chip,
    ControlledFormInput,
    DialogV2,
    EntityAutocomplete,
    TextAreaField,
    TransparentCard,
} from 'c-components';
import { useCommonTranslation } from 'c-translation';
import { IfHasAllPermissions } from 'c-auth-module/Components';
import { PermissionName } from 'c-sdk';
import type { Displays_Owner } from 'c-sdk';
import Card from '@mui/material/Card';
import { Box } from '@mui/material';
import type { User } from 'c-data';
import { useEntityData } from 'c-data';
import { useBoolean } from 'react-hanger';
import type { CampaignLink } from './campaignLinks';
import { addBlankCampaignLinks, campaignIdFromOption, parseCampaignIdList } from './campaignLinks';

type Props = {
    id?: number;
};

const UserAssociationAssignment: React.FC<Props> = ({ id }) => {
    const t = useCommonTranslation();
    const { getById } = useEntityData<User>('User');
    const user = getById({ id });
    const { keyedEntities: includedOwners } = useEntityData<Displays_Owner>('Owner');
    const { keyedEntities: displayOwners, upsertEntities: cacheDisplayOwners } =
        useEntityData<Displays_Owner>('Displays_Owner');
    const ownerIds = useWatch<{ owners?: number[] }, 'owners'>({ name: 'owners' });

    useEffect(() => {
        // User includes are normalised under the legacy Owner alias, while the autocomplete
        // searches Displays_Owner. Reuse those records without changing its API endpoint.
        const missingOwners = (ownerIds ?? []).flatMap(ownerId => {
            const owner = includedOwners[ownerId];
            return owner != null && displayOwners[ownerId] == null ? [owner] : [];
        });
        if (missingOwners.length > 0) cacheDisplayOwners(missingOwners);
    }, [ownerIds, includedOwners, displayOwners, cacheDisplayOwners]);

    const { getById: getCampaignById } = useEntityData('Campaign');
    const { hasAdminPermission } = useUserPermissions();
    const campaignLinks = useWatch<{ campaignUser?: CampaignLink[] }, 'campaignUser'>({
        name: 'campaignUser',
    });

    useEffect(() => {
        if (!id || !hasAdminPermission || !Array.isArray(campaignLinks)) return;
        // The user includes link rows, not Campaign entities. Search results are paginated, so
        // explicitly load each selected campaign to make its autocomplete option available.
        new Set(campaignLinks.map(link => link.campaign_id)).forEach(campaignId => {
            getCampaignById({ id: campaignId });
        });
    }, [id, hasAdminPermission, campaignLinks, getCampaignById]);

    const bulkCampaignDialogState = useBoolean(false);
    const [bulkCampaignText, setBulkCampaignText] = useState('');

    return (
        <>
            <Card>
                <Box p={2}>
                    <TransparentCard
                        title={t('Modules.Admin.UserForm.assignAssociations')}
                        titleTypographyProps={{ variant: 'h3' }}
                        subTitle={t('Modules.Admin.UserForm.associationsDescription')}
                    >
                        <AutoGrid spacing={2} xs={12} sm={6} md={4} lg={3}>
                            <IfHasAllPermissions
                                permissions={[
                                    PermissionName.UniledportalBuyerRead,
                                    PermissionName.UniledportalBuyer_userCreate,
                                ]}
                            >
                                <ControlledFormInput
                                    name="buyers"
                                    render={({ field }) => (
                                        <EntityAutocomplete
                                            {...field}
                                            multiple
                                            entityName="Buyer"
                                            textFieldProps={{
                                                label: t('Modules.Admin.UserForm.buyers'),
                                            }}
                                            searchColumns="name"
                                            labelColumn="name"
                                        />
                                    )}
                                />
                            </IfHasAllPermissions>
                            <IfHasAllPermissions
                                permissions={[
                                    PermissionName.UniledportalAgencyRead,
                                    PermissionName.UniledportalAgency_userCreate,
                                ]}
                            >
                                <ControlledFormInput
                                    name="agencies"
                                    render={({ field }) => (
                                        <EntityAutocomplete
                                            {...field}
                                            multiple
                                            entityName="Agency"
                                            textFieldProps={{
                                                label: t('Modules.Admin.UserForm.agencies'),
                                            }}
                                            searchColumns="name"
                                            labelColumn="name"
                                        />
                                    )}
                                />
                            </IfHasAllPermissions>
                            <IfHasAllPermissions
                                permissions={[
                                    PermissionName.UniledportalClientRead,
                                    PermissionName.UniledportalClient_userCreate,
                                ]}
                            >
                                <ControlledFormInput
                                    name="clients"
                                    render={({ field }) => (
                                        <EntityAutocomplete
                                            {...field}
                                            multiple
                                            entityName="Client"
                                            textFieldProps={{
                                                label: t('Modules.Admin.UserForm.brands'),
                                            }}
                                            searchColumns="name"
                                            labelColumn="name"
                                        />
                                    )}
                                />
                            </IfHasAllPermissions>
                            <IfHasAllPermissions
                                permissions={[
                                    PermissionName.DisplaysOwnersRead,
                                    PermissionName.UniledportalOwner_userCreate,
                                ]}
                            >
                                <ControlledFormInput
                                    name="owners"
                                    render={({ field }) => (
                                        <EntityAutocomplete
                                            {...field}
                                            multiple
                                            entityName="Displays_Owner"
                                            textFieldProps={{
                                                label: t('Modules.Admin.UserForm.owners'),
                                            }}
                                            searchColumns="name"
                                            labelColumn="name"
                                        />
                                    )}
                                />
                            </IfHasAllPermissions>
                            {id && (
                                <IfHasAllPermissions permissions={[PermissionName.Admin]}>
                                    <ControlledFormInput
                                        name="campaignUser"
                                        render={({ field }) => {
                                            const items: CampaignLink[] = Array.isArray(field.value)
                                                ? field.value
                                                : [];

                                            const ids = items.map(cu => cu.campaign_id);

                                            const appendBlank = (opt: any) => {
                                                const next = addBlankCampaignLinks(items, [
                                                    campaignIdFromOption(opt),
                                                ]);
                                                if (next === items) return;
                                                field.onChange(next);
                                            };

                                            const removeAt = (idx: number) => {
                                                const next = items.slice();
                                                next.splice(idx, 1);
                                                field.onChange(next);
                                            };

                                            const handleBulkApply = () => {
                                                field.onChange(
                                                    addBlankCampaignLinks(
                                                        items,
                                                        parseCampaignIdList(bulkCampaignText),
                                                    ),
                                                );
                                                setBulkCampaignText('');
                                                bulkCampaignDialogState.setFalse();
                                            };

                                            return (
                                                <>
                                                    <EntityAutocomplete
                                                        entityName="Campaign"
                                                        multiple
                                                        labelColumn="id"
                                                        searchColumns={['name', 'id']}
                                                        textFieldProps={{
                                                            label: t(
                                                                'Modules.Admin.UserForm.campaigns',
                                                            ),
                                                        }}
                                                        value={ids}
                                                        filterSelectedOptions={false}
                                                        disableCloseOnSelect
                                                        getOptionLabel={(opt: any) =>
                                                            String(
                                                                opt?.label ??
                                                                    opt?.name ??
                                                                    opt?.id ??
                                                                    opt ??
                                                                    '',
                                                            )
                                                        }
                                                        renderOption={(optionProps, option) => (
                                                            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                                                            <li
                                                                {...optionProps}
                                                                onMouseDown={(e: any) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    appendBlank(option);
                                                                }}
                                                                onTouchStart={(e: any) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    appendBlank(option);
                                                                }}
                                                            >
                                                                {option?.label ?? String(option)}
                                                            </li>
                                                        )}
                                                        renderTags={(_selected, getTagProps) =>
                                                            items.map((cu, idx) => {
                                                                const type = cu.link_type ?? '';
                                                                const label = type
                                                                    ? `${cu.campaign_id}: ${type}`
                                                                    : `${cu.campaign_id}`;
                                                                const tagProps = getTagProps({
                                                                    index: idx,
                                                                });
                                                                return (
                                                                    <Chip
                                                                        {...tagProps}
                                                                        // eslint-disable-next-line react/no-array-index-key
                                                                        key={`${cu.campaign_id}:${type}:${idx}`}
                                                                        label={label}
                                                                        color="primary"
                                                                        onDelete={e => {
                                                                            e?.stopPropagation?.();
                                                                            removeAt(idx);
                                                                        }}
                                                                    />
                                                                );
                                                            })
                                                        }
                                                        onChange={() => {}}
                                                    />

                                                    <Box mt={1}>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={
                                                                bulkCampaignDialogState.setTrue
                                                            }
                                                        >
                                                            {t(
                                                                'Modules.Admin.UserForm.bulkAddCampaigns',
                                                            )}
                                                        </Button>
                                                    </Box>

                                                    <DialogV2
                                                        open={bulkCampaignDialogState.value}
                                                        onClose={bulkCampaignDialogState.setFalse}
                                                        title={t(
                                                            'Modules.Admin.UserForm.bulkAddCampaigns',
                                                        )}
                                                        maxWidth="sm"
                                                    >
                                                        <TextAreaField
                                                            multiline
                                                            fullWidth
                                                            rows={6}
                                                            value={bulkCampaignText}
                                                            onChange={e =>
                                                                setBulkCampaignText(e.target.value)
                                                            }
                                                            placeholder={t(
                                                                'Modules.Admin.UserForm.bulkAddCampaignsPlaceholder',
                                                            )}
                                                        />
                                                        <Box
                                                            mt={2}
                                                            display="flex"
                                                            justifyContent="flex-end"
                                                            gap={1}
                                                        >
                                                            <Button
                                                                onClick={
                                                                    bulkCampaignDialogState.setFalse
                                                                }
                                                            >
                                                                {t('Modules.Admin.UserForm.cancel')}
                                                            </Button>
                                                            <Button
                                                                variant="contained"
                                                                onClick={handleBulkApply}
                                                            >
                                                                {t('Modules.Admin.UserForm.apply')}
                                                            </Button>
                                                        </Box>
                                                    </DialogV2>
                                                </>
                                            );
                                        }}
                                    />
                                </IfHasAllPermissions>
                            )}
                        </AutoGrid>
                    </TransparentCard>
                </Box>
            </Card>
            <IfHasAllPermissions permissions={[PermissionName.UniledportalRoleRead]}>
                <Box pt={2}>
                    <Card>
                        <Box p={2}>
                            <TransparentCard
                                title={t('Modules.Admin.UserForm.roles')}
                                titleTypographyProps={{ variant: 'h3' }}
                                subTitle={t('Modules.Admin.UserForm.roleDescription')}
                            >
                                <AutoGrid spacing={2} xs={12}>
                                    <ControlledFormInput
                                        name="roles"
                                        render={({ field }) => (
                                            <EntityAutocomplete
                                                {...field}
                                                multiple
                                                entityName="Role"
                                                textFieldProps={{
                                                    label: t('Modules.Admin.UserForm.roles'),
                                                }}
                                                searchColumns="name"
                                                labelColumn="name"
                                            />
                                        )}
                                    />
                                </AutoGrid>
                            </TransparentCard>
                            <IfHasAllPermissions
                                permissions={[PermissionName.Admin]}
                                otherwise={
                                    <Checkbox
                                        isBoolean
                                        label={t('Modules.Admin.UserForm.oldPortalDisabled')}
                                        value={user?.old_portal_disable}
                                        disabled
                                        onChange={() => {}}
                                    />
                                }
                            >
                                <ControlledFormInput
                                    name="old_portal_disable"
                                    render={({ field }) => (
                                        <Checkbox
                                            {...field}
                                            isBoolean
                                            label={t('Modules.Admin.UserForm.oldPortalDisabled')}
                                        />
                                    )}
                                />
                            </IfHasAllPermissions>{' '}
                            <IfHasAllPermissions
                                permissions={[PermissionName.Admin]}
                                otherwise={
                                    <Checkbox
                                        isBoolean
                                        label={t('Modules.Admin.UserForm.profilingEnabled')}
                                        value={user?.profiling}
                                        disabled
                                        onChange={() => {}}
                                    />
                                }
                            >
                                <ControlledFormInput
                                    name="profiling"
                                    render={({ field }) => (
                                        <Checkbox
                                            {...field}
                                            isBoolean
                                            label={t('Modules.Admin.UserForm.profilingEnabled')}
                                        />
                                    )}
                                />
                            </IfHasAllPermissions>
                        </Box>
                    </Card>
                </Box>
            </IfHasAllPermissions>
        </>
    );
};
export default UserAssociationAssignment;
